import React, { useState, useEffect } from 'react';
import { Eye, X, FileText, CheckCircle, Download, AlertCircle } from 'lucide-react';
import API from '../utils/api';

export default function DocModal({ owner, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/admin/owners/${owner._id}/documents`)
      .then(r => setData(r.data))
      .catch(err => {
        console.error('Doc load error:', err);
        setError(err.response?.data?.message || 'Failed to load documents');
      })
      .finally(() => setLoading(false));
  }, [owner._id]);

  const renderDoc = (doc, label) => {
    if (!doc?.url) return (
      <div style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 24, textAlign: 'center', color: 'var(--gray)' }}>
        <FileText size={32} style={{ marginBottom: 8, opacity: 0.35 }} />
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>Not uploaded yet</div>
      </div>
    );

    const isPDF = doc.filename?.toLowerCase().endsWith('.pdf') ||
      doc.url?.includes('application/pdf') || doc.url?.endsWith('.pdf');

    return (
      <div style={{ border: '1.5px solid var(--gray-border)', borderRadius: 12, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'var(--gray-light)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={14} color="var(--green)" />
            <span style={{ fontWeight: 700, fontSize: 13 }}>{label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {doc.filename && <span style={{ fontSize: 11, color: 'var(--gray)' }}>{doc.filename}</span>}
            <a href={doc.url} download={doc.filename || label}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--orange)', color: 'white', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
              <Download size={11} /> Save
            </a>
          </div>
        </div>

        {/* Content */}
        {isPDF ? (
          <div style={{ padding: 24, textAlign: 'center', background: '#FEF2F2' }}>
            <FileText size={40} color="var(--red)" style={{ marginBottom: 10 }} />
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>{doc.filename || 'PDF Document'}</div>
            <a href={doc.url} target="_blank" rel="noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--orange)', color: 'white', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              <Eye size={14} /> Open PDF
            </a>
          </div>
        ) : (
          <img
            src={doc.url}
            alt={label}
            style={{ width: '100%', maxHeight: 340, objectFit: 'contain', display: 'block', background: '#111', cursor: 'zoom-in' }}
            onClick={() => window.open(doc.url, '_blank')}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        )}
        {/* Image load error fallback */}
        <div style={{ display: 'none', padding: 20, textAlign: 'center', color: 'var(--gray)', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={24} color="var(--red)" />
          <div style={{ fontSize: 13 }}>Image could not be displayed</div>
          <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)', fontSize: 13, fontWeight: 600 }}>Open in new tab</a>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }} className="fade-in">

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
          <h3 style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Eye size={16} color="var(--orange)" /> Documents — {owner.shopName}
          </h3>
          <button onClick={onClose} style={{ background: 'var(--gray-light)', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', display: 'flex' }}><X size={16} /></button>
        </div>

        <div style={{ padding: 20 }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
          )}

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: 16, display: 'flex', gap: 10, color: 'var(--red)', fontSize: 14 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Failed to load documents</div>
                <div style={{ fontSize: 13 }}>{error}</div>
              </div>
            </div>
          )}

          {data && !loading && (
            <>
              {/* Owner Info */}
              <div style={{ background: 'var(--gray-light)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{data.shopName}</div>
                <div style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 6 }}>
                  {data.user?.name} · {data.user?.email} · {data.user?.phone}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: data.verificationStatus === 'approved' ? '#ECFDF5' : data.verificationStatus === 'rejected' ? '#FEF2F2' : '#FFFBEB', color: data.verificationStatus === 'approved' ? 'var(--green)' : data.verificationStatus === 'rejected' ? 'var(--red)' : '#F59E0B' }}>
                    {data.verificationStatus?.toUpperCase()}
                  </span>
                  {data.documents?.uploadedAt && (
                    <span style={{ fontSize: 11, color: 'var(--gray)' }}>
                      📎 Uploaded: {new Date(data.documents.uploadedAt).toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
              </div>

              {/* Documents */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {renderDoc(data.documents?.aadhar, 'Aadhar Card')}
                {renderDoc(data.documents?.workplace, 'Shop / Working Place Proof')}
              </div>

              {!data.documents?.aadhar?.url && !data.documents?.workplace?.url && (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--gray)', fontSize: 13 }}>
                  <AlertCircle size={24} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <div>No documents uploaded by this owner yet.</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>They can upload from their dashboard.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}